
import { translate } from 'sharedreact/utils/LanguageSupport';
import {
  isUnitValue, isUnitGreaterThanOrEqualTo, isUnitGreaterThan,
  isUnitLessThanOrEqualTo, isUnitLessThan
} from 'sharedreact/utils/UnitValidators';
import {
  isNumber, isNumberGreaterThanOrEqualTo, isNumberGreaterThan,
  isNumberLessThanOrEqualTo, isNumberLessThan
} from 'sharedreact/utils/NumberValidators';
import { isPercentage } from 'sharedreact/utils/PercentageValidators';
import { isValidAbsoluteFileUrl, isValidAbsoluteWindowsPath } from 'sharedreact/utils/UrlValidators';
import NumberSupport from 'sharedreact/utils/NumberSupport';
import UnitSupport from 'sharedreact/utils/UnitSupport';
import { Rule, ValidationState } from 'sharedreact/components/ValidationForm';

type RuleType = {
  rule: (value: string) => boolean,
  hint: (value: string) => string
};

const errorStrings: { [key: string]: string } = {
  mm: 'shared.validation.notavalidinput',
  cm: 'shared.validation.notavalidinput',
  mil: 'shared.validation.notavalidinput',
  inch: 'shared.validation.notavalidinput',
  point: 'shared.validation.notavalidinput',
  mg: 'shared.validation.notaweight',
  g: 'shared.validation.notaweight',
  kg: 'shared.validation.notaweight',
  dE: 'shared.validation.notadeltae',
  ppmm: 'shared.validation.notavalidinput',
  ppcm: 'shared.validation.notavalidinput',
  ppi: 'shared.validation.notavalidinput',
  gupi: 'shared.validation.notavalidinput',
  lpi: 'shared.validation.notavalidinput',
  deg: 'shared.validation.notavalidinput'
};

function getDefaultErrorString (inputUnit: string): string {
  console.error('ValidationRules: Missing errorString for unit ' + inputUnit);
  return 'shared.validation.notanumber';
}

function getErrorString (inputUnit: string): string {
  return errorStrings[inputUnit] || getDefaultErrorString(inputUnit);
}

type ValidationRules = {[rule: string]: Rule};

let validationRules: ValidationRules = {};

function passwordValidation (password?: ValidationState, passwordConfirm?: ValidationState): boolean {
    const isBothUsed = password != null &&
      passwordConfirm != null &&
      password.isUsed &&
      passwordConfirm.isUsed;
    const isBothChanged = isBothUsed &&
      password.isChanged &&
      passwordConfirm.isChanged;

    if (!isBothUsed || !isBothChanged) {
      return true;
    }

    return password.value === passwordConfirm.value;
}

Object.assign(validationRules, {
  required: {
    // value might be a number -> force to string
    rule: (value: any) => (value != null ? value.toString().trim() : ''),
    hint: (value: any) => translate('shared.validation.required')
  },
  int: {
    rule: (value: any) => /^(?:[-+]?(?:0|[1-9][0-9]*))$/.test(value),
    hint: (value: any) => translate('shared.validation.notaninteger', value)
  },
  password: {
    // rule function can accept argument components:
    // -> components registered to Form mapped by name
    rule: (value: string, components: any) => {
      const passwordConfirm = components.passwordConfirm.state;
      return passwordValidation({isUsed: true, isChanged: true, value: value}, passwordConfirm);
    },
    hint: (value: string) => translate('shared.validation.passwordsnotequal')
  },
  passwordConfirm: {
    // rule function can accept argument components:
    // -> components registered to Form mapped by name
    rule: (value: string, components: any) => {
      const password = components.password.state;
      return passwordValidation(password, {isUsed: true, isChanged: true, value: value});
    },
    hint: (value: string) => translate('shared.validation.passwordsnotequal')
  }
});

function addUnitRules (
  ruleName: string,
  inputUnit: string,
  displayUnit: string,
  greaterThanOrEqualTo?: number,
  greaterThan?: number,
  lessThanOrEqualTo?: number,
  lessThan?: number,
  precision?: number
): string[] {
  let rule: {[name: string]: RuleType} = {};

  const unitPrecision: number = precision != null ? precision : NumberSupport.defaultPrecision;

  let ruleNames = [ruleName];
  rule[ruleName] = {
    rule: (valueString: string) =>
      isUnitValue(valueString, displayUnit, inputUnit),
    hint: (valueString: string) => translate(
      getErrorString(inputUnit),
      valueString
    )
  };
  
  if (greaterThanOrEqualTo != null) {
    ruleNames.push(ruleName + '_minimum');
    rule[ruleName + '_minimum'] = {
      rule: (valueString: string) =>
        isUnitGreaterThanOrEqualTo(valueString, displayUnit, inputUnit, greaterThanOrEqualTo || 0, unitPrecision),
      hint: (valueString: string) => translate(
        'shared.validation.notgreaterthanorequalto',
        valueString,
        UnitSupport.numberToUnitString(greaterThanOrEqualTo || 0, inputUnit, displayUnit, unitPrecision)
      )
    };
  }

  if (greaterThan != null) {
    ruleNames.push(ruleName + '_minimum');
    rule[ruleName + '_minimum'] = {
      rule: (valueString: string) =>
        isUnitGreaterThan(valueString, displayUnit, inputUnit, greaterThan || 0, unitPrecision),
      hint: (valueString: string) => translate(
        'shared.validation.notgreaterthan',
        valueString,
        UnitSupport.numberToUnitString(greaterThan || 0, inputUnit, displayUnit, unitPrecision)
      )
    };
  }

  if (lessThanOrEqualTo != null) {
    ruleNames.push(ruleName + '_maximum');
    rule[ruleName + '_maximum'] = {
      rule: (valueString: string) =>
        isUnitLessThanOrEqualTo(valueString, displayUnit, inputUnit, lessThanOrEqualTo || 0, unitPrecision),
      hint: (valueString: string) => translate(
        'shared.validation.notlessthanorequalto',
        valueString,
        UnitSupport.numberToUnitString(lessThanOrEqualTo || 0, inputUnit, displayUnit, unitPrecision)
      )
    };
  }

  if (lessThan != null) {
    ruleNames.push(ruleName + '_maximum');
    rule[ruleName + '_maximum'] = {
      rule: (valueString: string) =>
        isUnitLessThan(valueString, displayUnit, inputUnit, lessThan || 0, unitPrecision),
      hint: (valueString: string) => translate(
        'shared.validation.notlessthan',
        valueString,
        UnitSupport.numberToUnitString(lessThan || 0, inputUnit, displayUnit, unitPrecision)
      )
    };
  }

  Object.assign(validationRules, rule);
  return ruleNames;
}

function addNumberRules (
  ruleName: string,
  greaterThanOrEqualTo?: number,
  greaterThan?: number,
  lessThanOrEqualTo?: number,
  lessThan?: number,
  precision?: number
): string[] {
  let rule: {[name: string]:  RuleType} = {};

  const numberPrecision: number = precision != null ? precision : NumberSupport.defaultPrecision;

  let ruleNames = [ruleName];
  rule[ruleName] = {
    rule: (valueString: string) => isNumber(valueString),
    hint: (valueString: string) => translate(
      'shared.validation.notanumber',
      valueString
    )
  };

  if (greaterThanOrEqualTo != null) {
    ruleNames.push(ruleName + '_minimum');
    rule[ruleName + '_minimum'] = {
      rule: (valueString: string) =>
        isNumberGreaterThanOrEqualTo(valueString, greaterThanOrEqualTo || 0, numberPrecision),
      hint: (valueString: string) => translate(
        'shared.validation.notgreaterthanorequalto',
        valueString,
        NumberSupport.numberToString(greaterThanOrEqualTo || 0, numberPrecision)
      )
    };
  }

  if (greaterThan != null) {
    ruleNames.push(ruleName + '_minimum');
    rule[ruleName + '_minimum'] = {
      rule: (valueString: string) =>
        isNumberGreaterThan(valueString, greaterThan || 0, numberPrecision),
      hint: (valueString: string) => translate(
        'shared.validation.notgreaterthan',
        valueString,
        NumberSupport.numberToString(greaterThan || 0, numberPrecision)
      )
    };
  }

  if (lessThanOrEqualTo != null) {
    ruleNames.push(ruleName + '_maximum');
    rule[ruleName + '_maximum'] = {
      rule: (valueString: string) =>
        isNumberLessThanOrEqualTo(valueString, lessThanOrEqualTo || 0, numberPrecision),
      hint: (valueString: string) => translate(
        'shared.validation.notlessthanorequalto',
        valueString,
        NumberSupport.numberToString(lessThanOrEqualTo || 0, numberPrecision)
      )
    };
  }

  if (lessThan != null) {
    ruleNames.push(ruleName + '_maximum');
    rule[ruleName + '_maximum'] = {
      rule: (valueString: string) =>
        isNumberLessThan(valueString, lessThan || 0, numberPrecision),
      hint: (valueString: string) => translate(
        'shared.validation.notlessthan',
        valueString,
        NumberSupport.numberToString(lessThan || 0, numberPrecision)
      )
    };
  }
  Object.assign(validationRules, rule);
  return ruleNames;
}

function addUniqueNameRule (
  ruleName: string,
  names: string[]
): string[] {
  let rule: {[name: string]:  RuleType} = {};

  let ruleNames = [ruleName];
  rule[ruleName] = {
    rule: (valueString: string) => {
      // Check if name already occurs in names
      if (names) {
        const equalNames = names.filter((n: string) =>
          n.toLocaleUpperCase() === valueString.toLocaleUpperCase()
        );
        if (equalNames.length > 0) {
          return false;
        }
      }

      return true;
    },
    hint: (valueString: string) => translate(
      'shared.validation.equalnames',
      valueString
    )
  };

  Object.assign(validationRules, rule);
  return ruleNames;
}

function addPercentageRules (ruleName: string): string[] {
  let rule: {[name: string]:  RuleType} = {};

  let ruleNames = [ruleName];
  rule[ruleName] = {
    rule: isPercentage,
    hint: (valueString: string) => translate(
      'shared.validation.notavalidinput',
      valueString
    )
  };

  Object.assign(validationRules, rule);
  return ruleNames;
}

function addFileUrlRules (ruleName: string): string[] {
  let rule: {[name: string]:  RuleType} = {};

  let ruleNames = [ruleName];
  rule[ruleName] = {
    rule: isValidAbsoluteFileUrl,
    hint: (valueString: string) => translate(
      'shared.validation.notavalidinput',
      valueString
    )
  };

  Object.assign(validationRules, rule);
  return ruleNames;
}

function addWindowsPathRules (ruleName: string): string[] {
  let rule: {[name: string]:  RuleType} = {};

  let ruleNames = [ruleName];
  rule[ruleName] = {
    rule: isValidAbsoluteWindowsPath,
    hint: (valueString: string) => translate(
      'shared.validation.notavalidinput',
      valueString
    )
  };

  Object.assign(validationRules, rule);
  return ruleNames;
}

function addStringRules (
  ruleName: string,
  maxLength: number): string[] {
  let rule: {[name: string]:  RuleType} = {};

  let ruleNames = [ruleName];
  rule[ruleName] = {
    rule: (valueString: string) => valueString.length <= maxLength,
    hint: (valueString: string) => translate(
      'shared.validation.notlongerthan',
      valueString,
      maxLength
    )
  };

  Object.assign(validationRules, rule);
  return ruleNames;
}

function addCustomRule (ruleName: string,
  ruleFunction: (valueString: string) => boolean,
  hintFunction: (valueString: string) => string): string[] {
  let rule: {[name: string]:  RuleType} = {};

  let ruleNames = [ruleName];
  rule[ruleName] = {
    rule: ruleFunction,
    hint: hintFunction
  };

  Object.assign(validationRules, rule);
  return ruleNames;
}

function getValidationRules () {
  return validationRules;
}

export { getValidationRules };

export default {
  addUnitRules,
  addNumberRules,
  addUniqueNameRule,
  addPercentageRules,
  addFileUrlRules,
  addWindowsPathRules,
  addStringRules,
  addCustomRule
};
